import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button, Combobox, Input, Textarea } from "../../ui";
import { fetchCompanies } from "../../../Layouts/crm/services/companiesService";
import { fetchContacts } from "../../../Layouts/crm/services/contactsService";
import { useAllUnits } from "../../../features/units/queries";
import { useRoutesList } from "../../../features/routes/queries";
import { useCostProfiles } from "../../../features/costProfiles/queries";
import { VEHICLE_CLASSES, type VehicleClass } from "../../../features/quotes/types";
import { FINANZAS_OPERATIVAS_BASE } from "../../../Layouts/finanzas-operativas/paths";
import type { QuoteWizard } from "../useQuoteWizard";
import { isoToLocalInput, localInputToIso, routeEndpoints, sourceLabel, type QuoteSource } from "../quoteSource";

interface Option {
  value: string;
  label: string;
}

interface ServiceStepProps {
  wizard: QuoteWizard;
  source: QuoteSource;
}

function SectionTitle({ children }: { children: string }) {
  return <h4 className="mt-4 border-b border-line pb-2 text-sm font-semibold text-content first:mt-0">{children}</h4>;
}

/** Paso 1 — cliente, servicio, horario y carga. */
export function ServiceStep({ wizard, source }: ServiceStepProps) {
  const { t } = useTranslation("finanzasOperativas");
  const navigate = useNavigate();
  const { service, setService } = wizard;

  const companiesQ = useQuery({
    queryKey: ["crm", "companies", "forQuotes"],
    queryFn: () => fetchCompanies({ page: 1, limit: 200 }),
    staleTime: 60_000,
  });
  const contactsQ = useQuery({
    queryKey: ["crm", "contacts", "forQuotes", service.crm_company_id],
    queryFn: () => fetchContacts({ page: 1, limit: 100, companyId: service.crm_company_id ?? undefined }),
    enabled: !!service.crm_company_id,
    staleTime: 60_000,
  });
  const unitsQ = useAllUnits();
  const profilesQ = useCostProfiles();
  const routesQ = useRoutesList({ page: 1, limit: 200 }, { enabled: source.kind === "blank" });

  const companyOptions: Option[] = useMemo(
    () => (companiesQ.data?.data ?? []).map((c) => ({ value: c.id, label: c.name })),
    [companiesQ.data],
  );
  const contactOptions: Option[] = useMemo(
    () => (contactsQ.data?.data ?? []).map((c) => ({ value: c.id, label: [c.name, c.email].filter(Boolean).join(" · ") || c.id })),
    [contactsQ.data],
  );
  const unitOptions: Option[] = useMemo(
    () =>
      ((unitsQ.data ?? []) as { id: string; name?: string; plate?: string }[]).map((u) => ({
        value: u.id,
        label: [u.name, u.plate].filter(Boolean).join(" · ") || u.id,
      })),
    [unitsQ.data],
  );
  const classOptions: Option[] = useMemo(
    () => VEHICLE_CLASSES.map((id) => ({ value: id, label: t(`costProfiles.vehicleClasses.${id}`) })),
    [t],
  );
  const profileOptions: Option[] = useMemo(() => {
    const all = profilesQ.data ?? [];
    const filtered = service.vehicle_class ? all.filter((p) => p.vehicle_class === service.vehicle_class) : all;
    return filtered.map((p) => ({ value: p.id, label: `${p.name}${p.is_default ? ` · ${t("costProfiles.default")}` : ""}` }));
  }, [profilesQ.data, service.vehicle_class, t]);
  const routeOptions: Option[] = useMemo(
    () => ((routesQ.data?.data ?? []) as { id: string; name: string }[]).map((r) => ({ value: r.id, label: r.name })),
    [routesQ.data],
  );

  const pickUnit = (unitId: string | null) => {
    const unit = ((unitsQ.data ?? []) as { id: string; vehicle_class?: VehicleClass }[]).find((u) => u.id === unitId);
    setService({
      ...service,
      unit_id: unitId,
      vehicle_class: unit?.vehicle_class && VEHICLE_CLASSES.includes(unit.vehicle_class) ? unit.vehicle_class : service.vehicle_class,
    });
  };

  const pickCompany = (companyId: string | null) => {
    const company = (companiesQ.data?.data ?? []).find((c) => c.id === companyId);
    setService({
      ...service,
      crm_company_id: companyId,
      crm_contact_id: null,
      customer_snapshot: { ...service.customer_snapshot, name: company?.name ?? null, contact_name: null, email: null, whatsapp: null },
    });
  };
  const pickContact = (contactId: string | null) => {
    const contact = (contactsQ.data?.data ?? []).find((c) => c.id === contactId);
    setService({
      ...service,
      crm_contact_id: contactId,
      customer_snapshot: {
        ...service.customer_snapshot,
        contact_name: contact?.name ?? null,
        email: contact?.email ?? null,
        whatsapp: contact?.whatsapp ?? contact?.phone ?? null,
      },
    });
  };

  const endpoints = source.kind === "route" ? routeEndpoints(source.route) : source.kind === "trip" ? routeEndpoints(source.route) : null;
  const label = sourceLabel(source);

  return (
    <div className="space-y-3">
      <SectionTitle>{t("quotes.service.customerTitle")}</SectionTitle>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-content">{t("quotes.fields.customer")}</label>
          <Combobox
            inputId="quote-customer"
            options={companyOptions}
            isClearable
            isLoading={companiesQ.isLoading}
            placeholder={companyOptions.length === 0 && !companiesQ.isLoading ? t("quotes.service.noCompanies") : t("quotes.service.customerPlaceholder")}
            value={companyOptions.find((o) => o.value === service.crm_company_id) ?? null}
            onChange={(opt) => pickCompany((opt as Option | null)?.value ?? null)}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-content">{t("quotes.fields.contact")}</label>
          <Combobox
            inputId="quote-contact"
            options={contactOptions}
            isClearable
            isDisabled={!service.crm_company_id}
            isLoading={contactsQ.isLoading}
            placeholder={t("quotes.service.contactPlaceholder")}
            value={contactOptions.find((o) => o.value === service.crm_contact_id) ?? null}
            onChange={(opt) => pickContact((opt as Option | null)?.value ?? null)}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Input
          label={t("quotes.service.customerName")}
          value={service.customer_snapshot.name ?? ""}
          onChange={(e) => setService({ ...service, customer_snapshot: { ...service.customer_snapshot, name: e.target.value } })}
        />
        <Input
          label={t("quotes.service.contactName")}
          value={service.customer_snapshot.contact_name ?? ""}
          onChange={(e) => setService({ ...service, customer_snapshot: { ...service.customer_snapshot, contact_name: e.target.value } })}
        />
        <Input
          label={t("quotes.service.customerEmail")}
          type="email"
          value={service.customer_snapshot.email ?? ""}
          onChange={(e) => setService({ ...service, customer_snapshot: { ...service.customer_snapshot, email: e.target.value } })}
        />
        <Input
          label={t("quotes.service.customerWhatsapp")}
          type="tel"
          value={service.customer_snapshot.whatsapp ?? ""}
          onChange={(e) => setService({ ...service, customer_snapshot: { ...service.customer_snapshot, whatsapp: e.target.value } })}
        />
      </div>

      <SectionTitle>{t("quotes.service.serviceTitle")}</SectionTitle>
      {source.kind !== "blank" && (
        <p className="text-xs text-content-secondary">
          <i className={`mr-1 ${source.kind === "route" ? "ri-route-line" : "ri-truck-line"}`} />
          {source.kind === "route" ? t("quotes.service.fromRoute") : t("quotes.service.fromTrip")}
          {label ? `: ${label}` : ""}
          {endpoints?.origin && endpoints?.destination && (
            <span className="ml-2 text-content-faint">
              {endpoints.origin.lat?.toFixed(4)}, {endpoints.origin.lng?.toFixed(4)} → {endpoints.destination.lat?.toFixed(4)}, {endpoints.destination.lng?.toFixed(4)}
            </span>
          )}
        </p>
      )}
      {source.kind === "blank" && (
        <div className="space-y-3">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium text-content">{t("quotes.fields.route")}</label>
            <Combobox
              inputId="quote-route"
              options={routeOptions}
              isClearable
              isLoading={routesQ.isLoading}
              placeholder={t("quotes.service.routePlaceholder")}
              value={routeOptions.find((o) => o.value === service.route_id) ?? null}
              onChange={(opt) => setService({ ...service, route_id: (opt as Option | null)?.value ?? null, origin: null, destination: null })}
              menuPortalTarget={document.body}
              menuPosition="fixed"
            />
          </div>
          {!service.route_id && (
            <>
              <p className="text-xs text-content-faint">{t("quotes.service.orAddresses")}</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <AddressFields
                  label={t("quotes.fields.origin")}
                  placeholder={t("quotes.service.originPlaceholder")}
                  point={service.origin}
                  onChange={(origin) => setService({ ...service, origin })}
                  t={t}
                />
                <AddressFields
                  label={t("quotes.fields.destination")}
                  placeholder={t("quotes.service.destinationPlaceholder")}
                  point={service.destination}
                  onChange={(destination) => setService({ ...service, destination })}
                  t={t}
                />
              </div>
            </>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-content">{t("quotes.fields.unit")}</label>
          <Combobox
            inputId="quote-unit"
            options={unitOptions}
            isClearable
            isLoading={unitsQ.isLoading}
            placeholder={t("quotes.service.unitPlaceholder")}
            value={unitOptions.find((o) => o.value === service.unit_id) ?? null}
            onChange={(opt) => pickUnit((opt as Option | null)?.value ?? null)}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
          <p className="text-xs text-content-faint">{t("quotes.service.unitHint")}</p>
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium text-content">{t("quotes.fields.vehicleClass")}</label>
          <Combobox
            inputId="quote-class"
            options={classOptions}
            isClearable
            placeholder={t("quotes.fields.vehicleClass")}
            value={classOptions.find((o) => o.value === service.vehicle_class) ?? null}
            onChange={(opt) => setService({ ...service, vehicle_class: ((opt as Option | null)?.value as VehicleClass) ?? null, cost_profile_id: null })}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>
        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-content">{t("quotes.fields.profile")}</label>
            <Button variant="link" size="xs" onClick={() => navigate(`${FINANZAS_OPERATIVAS_BASE}/costos`)}>
              {t("quotes.service.manageProfiles")}
            </Button>
          </div>
          <Combobox
            inputId="quote-profile"
            options={profileOptions}
            isClearable
            isLoading={profilesQ.isLoading}
            placeholder={profileOptions.length ? t("quotes.service.profilePlaceholder") : t("quotes.service.systemProfile")}
            value={profileOptions.find((o) => o.value === service.cost_profile_id) ?? null}
            onChange={(opt) => setService({ ...service, cost_profile_id: (opt as Option | null)?.value ?? null })}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>
      </div>

      <SectionTitle>{t("quotes.service.scheduleTitle")}</SectionTitle>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Input
          label={t("quotes.fields.pickupAt")}
          type="datetime-local"
          value={isoToLocalInput(service.schedule.pickup_at)}
          onChange={(e) => setService({ ...service, schedule: { ...service.schedule, pickup_at: localInputToIso(e.target.value) } })}
        />
        <Input
          label={t("quotes.fields.deliveryAt")}
          type="datetime-local"
          value={isoToLocalInput(service.schedule.delivery_at)}
          onChange={(e) => setService({ ...service, schedule: { ...service.schedule, delivery_at: localInputToIso(e.target.value) } })}
        />
      </div>

      <SectionTitle>{t("quotes.service.cargoTitle")}</SectionTitle>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Textarea
          containerClassName="sm:col-span-3"
          label={t("quotes.fields.cargoDescription")}
          rows={2}
          value={service.cargo.description ?? ""}
          onChange={(e) => setService({ ...service, cargo: { ...service.cargo, description: e.target.value } })}
        />
        <Input
          label={t("quotes.fields.weightKg")}
          type="number"
          min={0}
          value={service.cargo.weight_kg ?? ""}
          onChange={(e) => setService({ ...service, cargo: { ...service.cargo, weight_kg: e.target.value === "" ? null : Number(e.target.value) } })}
        />
        <Input
          label={t("quotes.fields.volumeM3")}
          type="number"
          min={0}
          step="0.1"
          value={service.cargo.volume_m3 ?? ""}
          onChange={(e) => setService({ ...service, cargo: { ...service.cargo, volume_m3: e.target.value === "" ? null : Number(e.target.value) } })}
        />
        <Input
          label={t("quotes.fields.declaredValue")}
          type="number"
          min={0}
          value={service.cargo.declared_value ?? ""}
          onChange={(e) => setService({ ...service, cargo: { ...service.cargo, declared_value: e.target.value === "" ? null : Number(e.target.value) } })}
        />
      </div>
    </div>
  );
}

interface AddressFieldsProps {
  label: string;
  placeholder: string;
  point: { lat?: number; lng?: number; address?: string } | null;
  onChange: (p: { lat?: number; lng?: number; address?: string } | null) => void;
  t: (k: string) => string;
}

/** Dirección + coordenadas manuales (sin autocompletar: el cálculo de casetas requiere lat/lng). */
function AddressFields({ label, placeholder, point, onChange, t }: AddressFieldsProps) {
  const p = point ?? {};
  return (
    <div className="space-y-2 rounded-lg border border-line p-3">
      <Input label={label} placeholder={placeholder} value={p.address ?? ""} onChange={(e) => onChange({ ...p, address: e.target.value })} />
      <div className="grid grid-cols-2 gap-2">
        <Input
          size="sm"
          label={`${t("quotes.service.coordinates")} · lat`}
          type="number"
          step="0.000001"
          value={p.lat ?? ""}
          onChange={(e) => onChange({ ...p, lat: e.target.value === "" ? undefined : Number(e.target.value) })}
        />
        <Input
          size="sm"
          label="lng"
          type="number"
          step="0.000001"
          value={p.lng ?? ""}
          onChange={(e) => onChange({ ...p, lng: e.target.value === "" ? undefined : Number(e.target.value) })}
        />
      </div>
    </div>
  );
}
